import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ToastMessage from "../../utils/toastMessage";
import { loginUser } from "../../store/actions/auth.action";
import { clearError } from "../../store/reducers/auth.reducer";
import { useAppSelector, useAppDispatch } from "../../store/index";
import { App, Button } from "antd";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import logoImage from "../../assets/images/logo.svg";
import theme from "../../themes";
import {
  authCardCss,
  authFieldRowCss,
  authLabelCss,
  authPageCss,
} from "../../styles/responsive";

// Validation schema
const validationSchema = yup.object({
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address")
    .max(100, "Email must be less than 100 characters"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),
});

type LoginFormData = yup.InferType<typeof validationSchema>;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
  } = useForm<LoginFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  // Show error toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error, 5);
    }
  }, [error]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await dispatch(loginUser(data)).unwrap();
      if (response.data.token) {
        toast.success("Login successful.", 5, <CheckCircleIcon />);
      } else {
        // Show success toast
        toast.success(
          "Please check your email for verification code.",
          5,
          <CheckCircleIcon />
        );
      }
      // Navigate to verification page
      navigate("/verify", { state: { email: data.email } });
    } catch (error) {
      // Error is already handled by the slice and will show as toast
      console.error("Login failed:", error);
    }
  };

  return (
    <LoginContainer>
      <LogoContainer>
        <LogoWrapper>
          <LogoImage src={logoImage} alt="Lytte.ai Logo" />
          <Title>Lytte.ai</Title>
        </LogoWrapper>
      </LogoContainer>

      <LoginCard>
        <WelcomeMessage>
          Welcome back! Please enter your email and password to login.
        </WelcomeMessage>

        <LoginForm onSubmit={handleSubmit(onSubmit)}>
          <FormGroup style={{ marginBottom: "16px" }}>
            <FieldRow>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                placeholder="Enter email address"
                disabled={loading}
                className={errors.email ? "error" : ""}
                {...register("email")}
              />
            </FieldRow>
          </FormGroup>

          <FormGroup>
            <FieldRow>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                placeholder="Enter password"
                disabled={loading}
                className={errors.password ? "error" : ""}
                {...register("password")}
              />
            </FieldRow>
          </FormGroup>

          {error && <FormError role="alert">{error}</FormError>}

          <Divider />

          <ButtonLinkRow>
            <ForgotLink to="/forgot-password">Forget password?</ForgotLink>
            <LoginButton
              htmlType="submit"
              disabled={loading || !isValid}
              className={isValid ? "active" : ""}
            >
              Login
            </LoginButton>
          </ButtonLinkRow>
        </LoginForm>
      </LoginCard>
    </LoginContainer>
  );
};

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  ${authPageCss}
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 640px) {
    margin-bottom: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  ${authCardCss}
`;

const WelcomeMessage = styled.p`
  text-align: left;
  margin: 0 0 32px 0;
  font-size: 18px;
  font-weight: 500;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldRow = styled.div`
  ${authFieldRowCss}
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 16px;
  text-align: left;
  ${authLabelCss}
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: white;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00b4d8;
  }

  &:hover {
    border-color: #bdbdbd;
  }

  &::placeholder {
    color: #9e9e9e;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

const ButtonLinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const LoginButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #047857 !important;
    color: #fff !important;
  }

  &:disabled {
    background: ${theme.colors.grey2};
    cursor: not-allowed;

    &:hover {
      background: ${theme.colors.grey2} !important;
      color: #fff !important;
    }
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 32px -16px 24px -16px;
`;

const FormError = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 24px;
`;

const ForgotLink = styled(Link)`
  color: #36a3e8;
  font-size: 16px;
  font-weight: 400;
`;

export default Login;
